import { Link, Outlet } from 'react-router';
import { Photo } from '@/components/Photo';
import { DraftMarker } from '@/content/DraftMarker';
import { STOREFRONT_NIGHT } from '@/content/ownerMedia';
import { LanguageSwitcher } from '@/design-system/components/LanguageSwitcher';
import { Logo } from '@/design-system/components/Logo';
import { ThemeToggle } from '@/design-system/components/ThemeToggle';
import { useTheme } from '@/features/theme/themeContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useI18n } from '@/i18n/context';
import styles from './AuthLayout.module.css';

/**
 * Sign-in, registration and password screens. Part of the public ("outside")
 * experience: a real photograph of an Al-Qinaa branch at night beside the
 * form sheet (owner-supplied, REAL_OWNER_PROVIDED — never an AI image or a
 * drawing).
 */
export function AuthLayout() {
  const { t } = useI18n();
  const { theme } = useTheme();
  // Phones: a smaller lockup keeps logo, language and display mode on one line.
  const narrow = useMediaQuery('(max-width: 479px)');
  const [line1, line2] = t.home.hero.title.split('\n');
  return (
    <div className={styles.shell}>
      <a href="#main" className="skip-link">
        {t.common.skipToContent}
      </a>
      <header className={styles.header}>
        <Link to="/" className={styles.brand} aria-label={t.nav.home}>
          <Logo tone={theme === 'dark' ? 'onDark' : 'onLight'} height={narrow ? 24 : 30} />
        </Link>
        <div className={styles.headerActions}>
          <LanguageSwitcher />
          <ThemeToggle />
          <Link to="/" className={styles.backLink}>
            {t.common.backToSite}
          </Link>
        </div>
      </header>

      <main id="main" className={styles.main} tabIndex={-1}>
        <aside className={styles.visual}>
          <div className={styles.visualCopy}>
            <p className={styles.eyebrow}>{t.common.partnerBrand}</p>
            <p className={styles.visualTitle}>
              <span>{line1}</span>
              <span className={styles.visualAccent}>{line2}</span>
            </p>
            <DraftMarker section="home.hero" tone="dark" />
          </div>
          <Photo photo={STOREFRONT_NIGHT} className={styles.photo} sizes="(min-width: 1024px) 55vw, 1px" />
        </aside>

        <section className={styles.panel}>
          <div className={styles.sheet}>
            <Outlet />
          </div>
        </section>
      </main>
    </div>
  );
}
